import React, { useState, useRef, useEffect } from 'react';
import { FileSpreadsheet, RefreshCw, ChevronDown, DownloadIcon} from 'lucide-react';
import { dataService, apiClient } from '@rti/shared';

const SPOT_FIELDS = [
  'spot_ethylene', 'ethane', 'propane', 'spot_rgp', 'spot_pgp',
  'spot_benzene', 'spot_styrene', 'spot_butadiene',
  'wti_crude_oil', 'brent_crude_oil', 'nat_gas', 'euro_to_us', 'us_to_ca', 'vam',
];
const CONTRACT_FIELDS = [
  'ethylene_contract', 'pgp_contract', 'benzene_contract',
  'styrene_contract', 'butadiene_contract',
];
const FIELD_LABELS: Record<string, string> = {
  spot_ethylene: 'Spot Ethylene', ethane: 'Ethane', propane: 'Propane',
  spot_rgp: 'Spot RGP', spot_pgp: 'Spot PGP', spot_benzene: 'Spot Benzene',
  spot_styrene: 'Spot Styrene', spot_butadiene: 'Spot Butadiene',
  wti_crude_oil: 'WTI Crude Oil', brent_crude_oil: 'Brent Crude Oil',
  nat_gas: 'Nat. Gas', euro_to_us: 'Euro to $US', us_to_ca: 'US to CA$', vam: 'VAM',
  ethylene_contract: 'Ethylene Contract', pgp_contract: 'PGP Contract',
  benzene_contract: 'Benzene Contract', styrene_contract: 'Styrene Contract',
  butadiene_contract: 'Butadiene Contract',
};
const YEAR_AVG_FIELDS = [
  'ethane', 'spot_ethylene', 'spot_rgp', 'spot_pgp',
  'spot_benzene', 'spot_styrene', 'spot_butadiene',
  'wti_crude_oil', 'brent_crude_oil', 'nat_gas', 'euro_to_us',
];

function getDateStr(row: any): string { return String(row?.date ?? '').split('T')[0]; }
function toNum(val: any): number | null { const n = parseFloat(val); return isNaN(n) ? null : n; }
function isWeekday(dateS: string): boolean {
  const [y, m, d] = dateS.split('-').map(Number);
  if (!y || !m || !d) return false;
  return ![0, 6].includes(new Date(Date.UTC(y, m - 1, d)).getUTCDay());
}
function avgField(rows: any[], field: string): number | null {
  const vals = rows.map(r => toNum(r[field])).filter((v): v is number => v != null);
  return vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null;
}
function isoWeekMonday(dateS: string): string {
  const [y, m, d] = dateS.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  const dow = dt.getUTCDay() || 7;
  dt.setUTCDate(dt.getUTCDate() - (dow - 1));
  return dt.toISOString().split('T')[0];
}

function applyDataHeader(cell: any, value: string, row?: any) {
  cell.value = value;
  cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF171A13' } };
  cell.font = { color: { argb: 'FFFFFFFF' }, bold: true, size: 12, name: 'Arial' };
  cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
  if (row) row.height = 44;
}

function applyRotatedHeader(cell: any, value: string, row?: any) {
  cell.value = value;
  cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF171A13' } };
  cell.font = { color: { argb: 'FFFFFFFF' }, bold: false, size: 10.5, name: 'Arial' };
  cell.alignment = { horizontal: 'center', vertical: 'middle', textRotation: 90, indent: 1 };
  if (row) row.height = 110;
}

function applySectionLabel(sheet: any, rowNum: number, startCol: number, endCol: number, label: string) {
  sheet.mergeCells(rowNum, startCol, rowNum, endCol);
  const cell = sheet.getCell(rowNum, startCol);
  cell.value = label;
  cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF80ED3F' } };
  cell.font = { color: { argb: 'FF000000' }, bold: true, size: 18, name: 'Arial' };
  cell.alignment = { horizontal: 'center', vertical: 'middle' };
  sheet.getRow(rowNum).height = 28;
}

function setColWidths(sheet: any, startCol: number, widths: number[]) {
  widths.forEach((w, i) => { sheet.getColumn(startCol + i).width = w; });
}


function applyGapCol(sheet: any, colNum: number, totalRows: number) {
  const RS_GREEN = { type: 'pattern' as const, pattern: 'solid' as const, fgColor: { argb: 'FF80ED3F' } };
  sheet.getColumn(colNum).width = 4;
  for (let r = 2; r <= totalRows; r++) {
    const cell = sheet.getCell(r, colNum);
    cell.fill = RS_GREEN;
    cell.border = { top: {}, left: {}, bottom: {}, right: {} };
  }
}

async function buildLogoImage(): Promise<string> {
  const resp = await fetch('/logos/Primary/LimeBlack.svg');
  const svgText = await resp.text();
  const blob = new Blob([svgText], { type: 'image/svg+xml' });
  const url = URL.createObjectURL(blob);
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = 532; canvas.height = 75;
      const ctx = canvas.getContext('2d')!;
      ctx.drawImage(img, 0, 0, 532, 75);
      URL.revokeObjectURL(url);
      resolve(canvas.toDataURL('image/png'));
    };
    img.src = url;
  });
}

const WHITE_BG: any = {
  id: 'whiteBg',
  beforeDraw(chart: any) {
    const { ctx, width, height } = chart;
    ctx.save(); ctx.fillStyle = '#FFFFFF'; ctx.fillRect(0, 0, width, height); ctx.restore();
  },
};
const PLOT_BORDER: any = {
  id: 'plotBorder',
  afterDraw(chart: any) {
    const { ctx, chartArea } = chart;
    if (!chartArea) return;
    ctx.save(); ctx.strokeStyle = '#000000'; ctx.lineWidth = 0.75;
    ctx.strokeRect(chartArea.left, chartArea.top, chartArea.width, chartArea.height);
    ctx.restore();
  },
};

function toMonthlyXY(rows: any[], field: string): { x: string; y: number }[] {
  const monthGroups = new Map<string, any[]>();
  rows.forEach(r => {
    if (!isWeekday(getDateStr(r)) || toNum(r[field]) == null) return;
    const monthKey = getDateStr(r).substring(0, 7);
    if (!monthGroups.has(monthKey)) monthGroups.set(monthKey, []);
    monthGroups.get(monthKey)!.push(r);
  });
  return [...monthGroups.entries()]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([month, monthRows]) => {
      const avg = avgField(monthRows, field);
      return { x: `${month}-15`, y: avg as number };
    })
    .filter(pt => pt.y != null);
}

function toContractXY(rows: any[], field: string, fromDate?: string): { x: string; y: number }[] {
  return rows
    .filter(r => toNum(r[field]) != null && (!fromDate || getDateStr(r) >= fromDate))
    .sort((a, b) => getDateStr(a).localeCompare(getDateStr(b)))
    .map(r => ({ x: getDateStr(r), y: toNum(r[field]) as number }));
}

function lineDs(label: string, data: any[], color: string, yAxisID = 'y', width = 1.5, extra: any = {}) {
  return {
    type: 'line' as const, label, data, borderColor: color, backgroundColor: color,
    borderWidth: width, pointRadius: 0, pointStyle: 'line' as const,
    spanGaps: true, tension: 0, yAxisID, ...extra,
  };
}
function contractLineDs(label: string, data: any[], color: string, yAxisID = 'y') {
  return {
    type: 'line' as const, label, data, borderColor: color, backgroundColor: color,
    borderWidth: 0, borderDash: [] as number[], pointRadius: 5, pointHoverRadius: 5,
    pointStyle: 'rectRot' as const, pointBackgroundColor: color, pointBorderColor: color,
    spanGaps: true, tension: 0, yAxisID, showLine: false,
  };
}

const dollarFmt = (v: any) => `$${Number(v).toFixed(2)}`;
const decimalFmt = (v: any) => Number(v).toFixed(3);

function chartCfg(
  title: string,
  datasets: any[],
  yLeftFmt: (v: any) => string,
  yRightFmt?: (v: any) => string,
): any {
  const scales: any = {
    x: {
      type: 'time',
      time: { unit: 'month', displayFormats: { month: 'MMM-yy' } },
      ticks: { maxTicksLimit: 24, maxRotation: 0, font: { size: 15, family: 'Arial' } },
      grid: { display: false },
      border: { color: '#888888' },
    },
    y: {
      position: 'left',
      grid: { color: '#D0D0D0', borderDash: [5, 5] },
      border: { color: '#888888' },
      ticks: { callback: yLeftFmt, font: { size: 15, family: 'Arial' } },
    },
  };
  if (yRightFmt) {
    scales.y2 = {
      position: 'right',
      grid: { display: false },
      border: { color: '#888888' },
      ticks: { callback: yRightFmt, font: { size: 15, family: 'Arial' } },
    };
  }
  return {
    type: 'line' as const,
    data: { datasets },
    options: {
      responsive: false,
      animation: false,
      layout: { padding: { top: 20, right: 60, bottom: 40, left: 60 } },
      plugins: {
        title: {
          display: true, text: title,
          font: { size: 20, family: 'Arial', weight: 'bold' as const },
          color: '#000000',
          padding: { top: 20, bottom: 30 },
        },
        legend: {
          position: 'top', align: 'center',
          labels: {
            usePointStyle: true, pointStyleWidth: 20, padding: 32,
            font: { size: 15, family: 'Arial', weight: 'bold' as const },
            color: '#000000',
          },
        },
        datalabels: { display: false },
        tooltip: { enabled: false },
      },
      scales,
    },
  };
}

function buildCrudeChart(p: any[], c?: any[], earliestDate?: string) {
  return chartCfg('Crude Oil & Nat. Gas ($/bbl - $/MMbtu)', [
    lineDs('Brent Crude Oil', toMonthlyXY(p, 'brent_crude_oil'), '#333333'),
    lineDs('WTI Crude Oil', toMonthlyXY(p, 'wti_crude_oil'), '#888888'),
    lineDs('Nat. Gas', toMonthlyXY(p, 'nat_gas'), '#80ED3F', 'y2'),
  ], dollarFmt, dollarFmt);
}
function buildEthaneChart(p: any[], c?: any[], earliestDate?: string) {
  return chartCfg('Ethane & Nat. Gas ($/gal - $/MMbtu)', [
    lineDs('Ethane', toMonthlyXY(p, 'ethane'), '#80ED3F'),
    lineDs('Nat. Gas', toMonthlyXY(p, 'nat_gas'), '#222222', 'y2'),
  ], dollarFmt, dollarFmt);
}
function buildEthyleneChart(p: any[], c: any[], earliestDate?: string) {
  return chartCfg('Ethylene & Ethane ($/lb - $/gal)', [
    contractLineDs('Ethylene Contract', toContractXY(c, 'ethylene_contract', earliestDate), '#555555'),
    lineDs('Spot Ethylene', toMonthlyXY(p, 'spot_ethylene'), '#AAAAAA'),
    lineDs('Ethane', toMonthlyXY(p, 'ethane'), '#80ED3F', 'y2'),
  ], dollarFmt, dollarFmt);
}
function buildPropaneChart(p: any[], c?: any[], earliestDate?: string) {
  return chartCfg('WTI Crude & Propane ($/bbl - $/gal)', [
    lineDs('WTI Crude Oil', toMonthlyXY(p, 'wti_crude_oil'), '#538135'),
    lineDs('Propane', toMonthlyXY(p, 'propane'), '#80ED3F', 'y2'),
  ], dollarFmt, dollarFmt);
}
function buildPropyleneChart(p: any[], c: any[], earliestDate?: string) {
  return chartCfg('Propylene ($/lb)', [
    contractLineDs('PGP Contract', toContractXY(c, 'pgp_contract', earliestDate), '#555555'),
    lineDs('Spot PGP', toMonthlyXY(p, 'spot_pgp'), '#AAAAAA'),
    lineDs('Spot RGP', toMonthlyXY(p, 'spot_rgp'), '#80ED3F'),
  ], dollarFmt);
}
function buildBenzeneChart(p: any[], c: any[], earliestDate?: string) {
  return chartCfg('Benzene & WTI Crude ($/gal - $/bbl)', [
    lineDs('WTI Crude Oil', toMonthlyXY(p, 'wti_crude_oil'), '#AAAAAA', 'y2'),
    contractLineDs('Benzene Contract', toContractXY(c, 'benzene_contract', earliestDate), '#80ED3F'),
    lineDs('Spot Benzene', toMonthlyXY(p, 'spot_benzene'), '#333333'),
  ], dollarFmt, dollarFmt);
}
function buildSmBdChart(p: any[], c: any[], earliestDate?: string) {
  const filterFrom2023 = (rows: any[]) => rows.filter(r => getDateStr(r) >= '2023-05');
  return chartCfg('Styrene & Butadiene ($/lb)', [
    contractLineDs('Styrene Contract', toContractXY(filterFrom2023(c), 'styrene_contract', earliestDate), '#555555'),
    lineDs('Spot Butadiene', toMonthlyXY(filterFrom2023(p), 'spot_butadiene'), '#AAAAAA'),
    contractLineDs('Butadiene Contract', toContractXY(filterFrom2023(c), 'butadiene_contract', earliestDate), '#80ED3F'),
    lineDs('WTI Crude Oil', toMonthlyXY(filterFrom2023(p), 'wti_crude_oil'), '#80ED3F', 'y', 1),
  ], dollarFmt);
}
function buildEuroUsdChart(p: any[], c?: any[], earliestDate?: string) {
  const cfg = chartCfg('Euro to US-Dollar Exchange Rate', [
    lineDs('EUR/USD', toMonthlyXY(p, 'euro_to_us'), '#80ED3F', 'y', 2),
  ], decimalFmt);
  cfg.options.scales.x.ticks.maxTicksLimit = 36;
  return cfg;
}

async function renderChart(config: any): Promise<string> {
  const { Chart, registerables } = await import('chart.js');
  Chart.register(...registerables);
  // @ts-ignore — module declaration exists in vite-env.d.ts
  await import('chartjs-adapter-date-fns');
  const canvas = document.createElement('canvas');
  canvas.width = 1440; canvas.height = 720;
  canvas.style.cssText = 'position:absolute;left:-9999px;top:-9999px;';
  document.body.appendChild(canvas);
  const chart = new Chart(canvas, {
    ...config,
    plugins: [WHITE_BG, PLOT_BORDER, ...(config.plugins ?? [])],
  });
  await new Promise<void>(resolve => requestAnimationFrame(() => requestAnimationFrame(() => resolve())));
  const base64 = canvas.toDataURL('image/png');
  chart.destroy();
  document.body.removeChild(canvas);
  return base64;
}

const LOGO_ROW_HEIGHT = 85;
const LOGO_DISPLAY_H = 75;
const LOGO_ROW_OFFSET = (LOGO_ROW_HEIGHT - LOGO_DISPLAY_H) / (LOGO_ROW_HEIGHT - 10);

function addChartSheet(workbook: any, name: string, imgBase64: string, logoId: number) {
  const sheet = workbook.addWorksheet(name);
  sheet.getRow(1).height = LOGO_ROW_HEIGHT;
  sheet.addImage(logoId, { tl: { col: 3.75, row: LOGO_ROW_OFFSET }, ext: { width: 533, height: LOGO_DISPLAY_H }, editAs: 'absolute' });
  const imgId = workbook.addImage({
    base64: imgBase64.replace(/^data:image\/png;base64,/, ''),
    extension: 'png',
  });
  sheet.addImage(imgId, { tl: { col: 0.1, row: 1.1 }, ext: { width: 1152, height: 576 }, editAs: 'oneCell' });
  for (let c = 1; c <= 12; c++) sheet.getColumn(c).width = 11;
  for (let r = 2; r <= 33; r++) sheet.getRow(r).height = 18;
  sheet.views = [{ state: 'normal', zoomScale: 175, showGridLines: false }];
}

function buildDataSheet(workbook: any, petchem: any[], contracts: any[], logoId: number) {
  const sheet = workbook.addWorksheet('Data', {
    properties: { tabColor: { argb: 'FF000000' } },
  });

  sheet.getRow(1).height = LOGO_ROW_HEIGHT;

  const HEADER_ROW = 2;
  const DATA_START = 3;
  const headerRow = sheet.getRow(HEADER_ROW);
  const leftHeaders = ['Date', ...SPOT_FIELDS.map(f => FIELD_LABELS[f])];
  leftHeaders.forEach((h, i) => applyDataHeader(sheet.getCell(HEADER_ROW, i + 1), h, headerRow));

  const spotRows = [...petchem]
    .filter(r => isWeekday(getDateStr(r)))
    .sort((a, b) => getDateStr(a).localeCompare(getDateStr(b)));

  spotRows.forEach((row, ri) => {
    const rowNum = DATA_START + ri;
    const dateCell = sheet.getCell(rowNum, 1);
    const [sy, sm, sd] = getDateStr(row).split('-').map(Number);
    dateCell.value = new Date(sy, sm - 1, sd);
    dateCell.numFmt = 'ddd m/d/yy';
    dateCell.alignment = { horizontal: 'center' };
    SPOT_FIELDS.forEach((field, fi) => {
      const cell = sheet.getCell(rowNum, fi + 2);
      const val = toNum(row[field]);
      if (val != null) {
        cell.value = val;
        cell.numFmt = (field === 'euro_to_us' || field === 'us_to_ca') ? '$#,##0.000' : '$#,##0.0000';
        cell.alignment = { horizontal: 'right' };
      }
    });
  });

  const CONTRACT_COL = leftHeaders.length + 2;
  const contractHeaders = ['Month Date', ...CONTRACT_FIELDS.map(f => FIELD_LABELS[f])];
  contractHeaders.forEach((h, i) => applyDataHeader(sheet.getCell(HEADER_ROW, CONTRACT_COL + i), h, headerRow));

  const contractRows = [...contracts].sort((a, b) => getDateStr(b).localeCompare(getDateStr(a)));
  contractRows.forEach((row, ri) => {
    const rowNum = DATA_START + ri;
    const dateCell = sheet.getCell(rowNum, CONTRACT_COL);
    const [cy, cm, cd] = getDateStr(row).split('-').map(Number);
    dateCell.value = new Date(cy, cm - 1, cd);
    dateCell.numFmt = 'mmm-yy';
    dateCell.alignment = { horizontal: 'center' };
    CONTRACT_FIELDS.forEach((field, fi) => {
      const cell = sheet.getCell(rowNum, CONTRACT_COL + fi + 1);
      const val = toNum(row[field]);
      if (val != null) {
        cell.value = val;
        cell.numFmt = '$#,##0.0000';
        cell.alignment = { horizontal: 'right' };
      }
    });
  });

  sheet.views = [{ state: 'frozen', xSplit: 0, ySplit: 2, topLeftCell: 'A3', activeCell: 'A1', showGridLines: true }];
  sheet.autoFilter = { from: { row: HEADER_ROW, column: 1 }, to: { row: HEADER_ROW, column: leftHeaders.length } };

  const colW = (text: string) => {
    const longestWord = text.split(' ').reduce((max, w) => Math.max(max, w.length), 0);
    return Math.max(13, longestWord + 3);
  };
  sheet.getColumn(1).width = colW('Date');
  leftHeaders.slice(1).forEach((h, i) => { sheet.getColumn(i + 2).width = colW(h); });
  sheet.getColumn(leftHeaders.length + 1).width = colW('Date');
  sheet.getColumn(CONTRACT_COL).width = colW('Month Date');
  contractHeaders.slice(1).forEach((h, i) => { sheet.getColumn(CONTRACT_COL + i + 1).width = colW(h); });

  const GROUP_BORDER = { style: 'thin' as const, color: { argb: 'FF000000' } };
  const totalRows = DATA_START + spotRows.length - 1;
  for (let r = HEADER_ROW; r <= totalRows; r++) {
    const pl = sheet.getCell(r, 1);
    pl.border = { ...pl.border, left: GROUP_BORDER };
    const pr = sheet.getCell(r, leftHeaders.length);
    pr.border = { ...pr.border, right: GROUP_BORDER };
    const cl = sheet.getCell(r, CONTRACT_COL);
    cl.border = { ...cl.border, left: GROUP_BORDER };
    const cr = sheet.getCell(r, CONTRACT_COL + contractHeaders.length - 1);
    cr.border = { ...cr.border, right: GROUP_BORDER };
  }

  sheet.addImage(logoId, { tl: { col: 8, row: LOGO_ROW_OFFSET }, ext: { width: 533, height: LOGO_DISPLAY_H }, editAs: 'absolute' });
}

function buildAveragesSheet(workbook: any, petchem: any[], contracts: any[], logoId: number) {
  const sheet = workbook.addWorksheet('Averages', {
    properties: { tabColor: { argb: 'FF70AD47' } },
  });

  const spotRows = [...petchem]
    .filter(r => isWeekday(getDateStr(r)))
    .sort((a, b) => getDateStr(a).localeCompare(getDateStr(b)));

  const contractByMonth: Record<string, any> = {};
  contracts.forEach(r => { contractByMonth[getDateStr(r).substring(0, 7)] = r; });

  sheet.getRow(1).height = LOGO_ROW_HEIGHT;

  const SECTION_LABEL_ROW = 2;
  const HEADER_ROW = 3;
  const DATA_START = 4;
  const headerRow = sheet.getRow(HEADER_ROW);

  const YEAR_COL = 1;
  const yearHeaders = ['Year', ...YEAR_AVG_FIELDS.map(f => FIELD_LABELS[f])];
  applySectionLabel(sheet, SECTION_LABEL_ROW, YEAR_COL, YEAR_COL + yearHeaders.length - 1, 'Year');
  yearHeaders.forEach((h, i) => applyRotatedHeader(sheet.getCell(HEADER_ROW, YEAR_COL + i), h, headerRow));

  const yearGroups = new Map<string, any[]>();
  spotRows.forEach(r => {
    const yr = getDateStr(r).substring(0, 4);
    if (!yearGroups.has(yr)) yearGroups.set(yr, []);
    yearGroups.get(yr)!.push(r);
  });
  [...yearGroups.entries()].sort((a, b) => b[0].localeCompare(a[0])).forEach(([yr, rows], ri) => {
    const rowNum = DATA_START + ri;
    sheet.getCell(rowNum, YEAR_COL).value = parseInt(yr);
    sheet.getCell(rowNum, YEAR_COL).alignment = { horizontal: 'center' };
    YEAR_AVG_FIELDS.forEach((field, fi) => {
      const val = avgField(rows, field);
      const cell = sheet.getCell(rowNum, YEAR_COL + fi + 1);
      if (val != null) { cell.value = val; cell.numFmt = '$#,##0.0000'; cell.alignment = { horizontal: 'right' }; }
    });
  });
  setColWidths(sheet, YEAR_COL, [10, ...YEAR_AVG_FIELDS.map(() => 10)]);

  const MONTH_COL = YEAR_COL + yearHeaders.length + 1;
  const monthHeaders = ['Month', ...SPOT_FIELDS.map(f => FIELD_LABELS[f]), ...CONTRACT_FIELDS.map(f => FIELD_LABELS[f]), 'Oil:N.Gas'];
  applySectionLabel(sheet, SECTION_LABEL_ROW, MONTH_COL, MONTH_COL + monthHeaders.length - 1, 'Month');
  monthHeaders.forEach((h, i) => applyRotatedHeader(sheet.getCell(HEADER_ROW, MONTH_COL + i), h, headerRow));

  const monthGroups = new Map<string, any[]>();
  spotRows.forEach(r => {
    const key = getDateStr(r).substring(0, 7);
    if (!monthGroups.has(key)) monthGroups.set(key, []);
    monthGroups.get(key)!.push(r);
  });
  [...monthGroups.entries()].sort((a, b) => b[0].localeCompare(a[0])).forEach(([month, rows], ri) => {
    const rowNum = DATA_START + ri;
    const [my, mm] = month.split('-').map(Number);
    const mDateCell = sheet.getCell(rowNum, MONTH_COL);
    mDateCell.value = new Date(my, mm - 1, 1); mDateCell.numFmt = 'mmm-yy'; mDateCell.alignment = { horizontal: 'center' };
    SPOT_FIELDS.forEach((field, fi) => {
      const val = avgField(rows, field);
      const cell = sheet.getCell(rowNum, MONTH_COL + fi + 1);
      if (val != null) { cell.value = val; cell.numFmt = '$#,##0.0000'; cell.alignment = { horizontal: 'right' }; }
    });
    const cRow = contractByMonth[month];
    CONTRACT_FIELDS.forEach((field, fi) => {
      const val = cRow ? toNum(cRow[field]) : null;
      const cell = sheet.getCell(rowNum, MONTH_COL + SPOT_FIELDS.length + fi + 1);
      if (val != null) { cell.value = val; cell.numFmt = '$#,##0.0000'; cell.alignment = { horizontal: 'right' }; }
    });
    const oilAvg = avgField(rows, 'wti_crude_oil'); const gasAvg = avgField(rows, 'nat_gas');
    const oilGasCell = sheet.getCell(rowNum, MONTH_COL + SPOT_FIELDS.length + CONTRACT_FIELDS.length + 1);
    if (oilAvg != null && gasAvg != null && gasAvg !== 0) {
      oilGasCell.value = oilAvg / gasAvg; oilGasCell.numFmt = '0.00'; oilGasCell.alignment = { horizontal: 'right' };
    }
  });
  setColWidths(sheet, MONTH_COL, [12, ...SPOT_FIELDS.map(() => 10), ...CONTRACT_FIELDS.map(() => 10), 10]);

  const WEEK_COL = MONTH_COL + monthHeaders.length + 1;
  const weekHeaders = ['Week', ...SPOT_FIELDS.map(f => FIELD_LABELS[f]), ...CONTRACT_FIELDS.map(f => FIELD_LABELS[f]), 'Oil:N.Gas'];
  applySectionLabel(sheet, SECTION_LABEL_ROW, WEEK_COL, WEEK_COL + weekHeaders.length - 1, 'Week');
  weekHeaders.forEach((h, i) => applyRotatedHeader(sheet.getCell(HEADER_ROW, WEEK_COL + i), h, headerRow));

  const weekGroups = new Map<string, any[]>();
  spotRows.forEach(r => {
    const key = isoWeekMonday(getDateStr(r));
    if (!weekGroups.has(key)) weekGroups.set(key, []);
    weekGroups.get(key)!.push(r);
  });
  [...weekGroups.entries()].sort((a, b) => b[0].localeCompare(a[0])).forEach(([weekStart, rows], ri) => {
    const rowNum = DATA_START + ri;
    const [wy, wm, wd] = weekStart.split('-').map(Number);
    const wDateCell = sheet.getCell(rowNum, WEEK_COL);
    wDateCell.value = new Date(wy, wm - 1, wd); wDateCell.numFmt = 'm/d/yyyy'; wDateCell.alignment = { horizontal: 'center' };
    SPOT_FIELDS.forEach((field, fi) => {
      const val = avgField(rows, field);
      const cell = sheet.getCell(rowNum, WEEK_COL + fi + 1);
      if (val != null) { cell.value = val; cell.numFmt = '$#,##0.0000'; cell.alignment = { horizontal: 'right' }; }
    });
    const cRow = contractByMonth[weekStart.substring(0, 7)];
    CONTRACT_FIELDS.forEach((field, fi) => {
      const val = cRow ? toNum(cRow[field]) : null;
      const cell = sheet.getCell(rowNum, WEEK_COL + SPOT_FIELDS.length + fi + 1);
      if (val != null) { cell.value = val; cell.numFmt = '$#,##0.0000'; cell.alignment = { horizontal: 'right' }; }
    });
    const oilAvg = avgField(rows, 'wti_crude_oil'); const gasAvg = avgField(rows, 'nat_gas');
    const oilGasCell = sheet.getCell(rowNum, WEEK_COL + SPOT_FIELDS.length + CONTRACT_FIELDS.length + 1);
    if (oilAvg != null && gasAvg != null && gasAvg !== 0) {
      oilGasCell.value = oilAvg / gasAvg; oilGasCell.numFmt = '0.00'; oilGasCell.alignment = { horizontal: 'right' };
    }
  });
  setColWidths(sheet, WEEK_COL, [13, ...SPOT_FIELDS.map(() => 10), ...CONTRACT_FIELDS.map(() => 10), 10]);

  sheet.views = [{ state: 'frozen', xSplit: 0, ySplit: 3, topLeftCell: 'A4', activeCell: 'A1', showGridLines: true }];

  const maxDataRows = Math.max(yearGroups.size, monthGroups.size, weekGroups.size);
  applyGapCol(sheet, YEAR_COL + yearHeaders.length, DATA_START + maxDataRows);
  applyGapCol(sheet, MONTH_COL + monthHeaders.length, DATA_START + maxDataRows);

  sheet.addImage(logoId, { tl: { col: 24, row: LOGO_ROW_OFFSET }, ext: { width: 533, height: LOGO_DISPLAY_H }, editAs: 'absolute' });
}

const PetchemExportButton: React.FC = () => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!showDropdown) return;
    const handleOutsideClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [showDropdown]);

  const handleExport = async () => {
    if (isGenerating) return;
    setIsGenerating(true);
    try {
      const [petchemRaw, contractsRaw] = await Promise.all([
        dataService.getPetchemData(),
        dataService.getContractsData(),
      ]);
      const petchem: any[] = Array.isArray(petchemRaw)
        ? [...petchemRaw].sort((a, b) => getDateStr(a).localeCompare(getDateStr(b)))
        : [];
      const contracts: any[] = Array.isArray(contractsRaw)
        ? [...contractsRaw].sort((a, b) => getDateStr(a).localeCompare(getDateStr(b)))
        : [];

      const ExcelJS = await import('exceljs');
      const workbook = new ExcelJS.Workbook();
      workbook.creator = 'ResinSmart';
      workbook.created = new Date();

      const logoBase64 = await buildLogoImage();
      const logoId = workbook.addImage({ base64: logoBase64.replace(/^data:image\/png;base64,/, ''), extension: 'png' });

      buildDataSheet(workbook, petchem, contracts, logoId);

      const earliestPetchemDate = petchem.length > 0 ? getDateStr(petchem[0]) : undefined;

      const chartDefs: { name: string; build: () => any }[] = [
        { name: 'Crude',     build: () => buildCrudeChart(petchem, contracts, earliestPetchemDate) },
        { name: 'Ethane',    build: () => buildEthaneChart(petchem, contracts, earliestPetchemDate) },
        { name: 'Ethylene',  build: () => buildEthyleneChart(petchem, contracts, earliestPetchemDate) },
        { name: 'Propane',   build: () => buildPropaneChart(petchem, contracts, earliestPetchemDate) },
        { name: 'Propylene', build: () => buildPropyleneChart(petchem, contracts, earliestPetchemDate) },
        { name: 'Benzene',   build: () => buildBenzeneChart(petchem, contracts, earliestPetchemDate) },
        { name: 'SM&BD',     build: () => buildSmBdChart(petchem, contracts, earliestPetchemDate) },
        { name: 'Euro-USD',  build: () => buildEuroUsdChart(petchem, contracts, earliestPetchemDate) },
      ];
      for (const { name, build } of chartDefs) {
        const base64 = await renderChart(build());
        addChartSheet(workbook, name, base64, logoId);
      }

      buildAveragesSheet(workbook, petchem, contracts, logoId);


      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });

      const today = new Date().toISOString().split('T')[0];
      const fileName = `ResinSmart-Petchem-${today.replace(/-/g, '')}.xlsx`;

      // Save to server cache (best-effort)
      try {
        await apiClient.savePetchemPdf(blob, fileName);
      } catch (err) {
        console.error('Failed to save xlsx to cache:', err);
      }

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Petchem export failed:', err);
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setShowDropdown(prev => !prev)}
        disabled={isGenerating}
        className={`flex items-center gap-1 px-3 py-2 rounded-md bg-rs-green !text-black font-medium transition-all hover:bg-rs-green/80 disabled:cursor-not-allowed ${isGenerating ? 'opacity-50' : ''}`}
        title="Download Daily Petchem Report (.xlsx)"
        aria-label="Download Daily Petchem Report"
      >
        {isGenerating ? (
          <RefreshCw size={15} className="flex-shrink-0 animate-spin" />
        ) : (
          <DownloadIcon size={20} className="flex-shrink-0" />
        )}
        <ChevronDown size={20} className={`transition-transform duration-150 ${showDropdown ? 'rotate-180' : ''}`} />
      </button>

      {showDropdown && (
        <div className="absolute right-0 top-full mt-1 bg-rs-jet border border-rs-off-black rounded-md shadow-xl z-10 min-w-[200px] overflow-hidden">
          <button
            onClick={() => { handleExport(); setShowDropdown(false); }}
            disabled={isGenerating}
            className="w-full flex items-center gap-3 px-4 py-3 text-sm text-rs-off-white hover:bg-rs-jet-light transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FileSpreadsheet className="w-4 h-4 text-rs-green" />
            Daily Petchem Report
          </button>
        </div>
      )}
    </div>
  );
};

export default PetchemExportButton;
